import md5 from 'blueimp-md5'
import {
  Avatar as ShadcnAvatar,
  AvatarImage,
  AvatarFallback,
} from '@/components/ui/shadcn/avatar'
import { cn } from '@/lib/utils'

type AvatarType = 'initials' | 'gravatar' | 'custom'

interface AvatarProps {
  name?: string
  email?: string
  src?: string
  avatarType?: AvatarType
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  fallbackClassName?: string
  showOnlineStatus?: boolean
  isOnline?: boolean
}

const sizeClasses = {
  xs: 'size-6 text-xs',
  sm: 'size-8 text-sm',
  md: 'size-10 text-base',
  lg: 'size-12 text-lg',
  xl: 'size-16 text-xl',
}

const statusSizeClasses = {
  xs: 'size-1.5 border',
  sm: 'size-2 border',
  md: 'size-2.5 border-2',
  lg: 'size-3 border-2',
  xl: 'size-4 border-2',
}

function getInitials(name?: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

function getGravatarUrl(email?: string, size: number = 80): string | undefined {
  if (!email) return undefined
  const hash = md5(email.toLowerCase().trim())
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=mp`
}

function getAvatarSrc(
  avatarType: AvatarType,
  src?: string,
  email?: string
): string | undefined {
  switch (avatarType) {
    case 'custom':
      return src
    case 'gravatar':
      return getGravatarUrl(email)
    case 'initials':
    default:
      return undefined
  }
}

export function Avatar({
  name,
  email,
  src,
  avatarType = 'initials',
  size = 'md',
  className,
  fallbackClassName,
  showOnlineStatus = false,
  isOnline = false,
}: AvatarProps) {
  const avatarSrc = getAvatarSrc(avatarType, src, email)
  const initials = getInitials(name)

  return (
    <div className="relative inline-block">
      <ShadcnAvatar className={cn(sizeClasses[size], className)}>
        {avatarSrc && (
          <AvatarImage src={avatarSrc} alt={name || 'Avatar'} />
        )}
        <AvatarFallback
          className={cn(
            'bg-primary/20 text-primary font-medium',
            fallbackClassName
          )}
        >
          {initials}
        </AvatarFallback>
      </ShadcnAvatar>
      {showOnlineStatus && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-background',
            statusSizeClasses[size],
            isOnline ? 'bg-success' : 'bg-muted-foreground'
          )}
        />
      )}
    </div>
  )
}

export default Avatar
